import { Button, Modal } from "antd";
import { useState } from "react";
import { FiCreditCard, FiShield, FiX, FiZap } from "react-icons/fi";
import { useSelector } from "react-redux";
import { t } from "../../translations";
import { createLogger } from "../../utils/logger";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import "./SubscriptionPaywallModal.css";

const logger = createLogger("SubscriptionPaywallModal");
const SubscriptionPaywallModal = ({ visible, onClose, onSubscribe }) => {
  const user = useSelector((state) => state.user);
  const lang = useSelector((state) => state.lang);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await MakeRequestAsync({
        type: "POST",
        url: service_api,
        route: "subscription/checkout",
        data: { chefId: user._id },
        token: user?.refresh_token
      });

      if (response.data.url) {
        // Open Stripe checkout in new tab
        const checkoutWindow = window.open(response.data.url, "_blank");

        // Optional: Listen for checkout completion
        const checkClosed = setInterval(() => {
          if (checkoutWindow.closed) {
            clearInterval(checkClosed);
            // Call the onSubscribe callback to refresh subscription status
            if (onSubscribe) {
              onSubscribe();
            } else {
              // Fallback to page reload if no callback provided
              window.location.reload();
            }
          }
        }, 1000);
      }
    } catch (err) {
      logger.error("Failed to create checkout session:", err);
      setError(
        err.response?.data?.message || t("subscription.checkout_error", lang)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await MakeRequestAsync({
        type: "POST",
        url: service_api,
        route: "subscription/portal",
        data: { chefId: user._id },
        token: user?.refresh_token
      });

      if (response.data.url) {
        window.open(response.data.url, "_blank");
      }
    } catch (err) {
      logger.error("Failed to create portal session:", err);
      setError(
        err.response?.data?.message || t("subscription.portal_error", lang)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={500}
      className="subscription-paywall-modal"
      closeIcon={<FiX size={24} />}
    >
      <div className="paywall-content">
        <div className="paywall-header">
          <div className="limit-reached-icon">
            <FiZap size={48} />
          </div>
          <h2>{t("subscription.free_limit_reached", lang)}</h2>
          <p className="limit-message">
            {t("subscription.free_limit_message", lang)}
          </p>
        </div>

        <div className="paywall-features">
          <div className="feature-item">
            <FiShield className="feature-icon" />
            <div className="feature-content">
              <h4>{t("subscription.unlimited_orders", lang)}</h4>
              <p>{t("subscription.unlimited_orders_desc", lang)}</p>
            </div>
          </div>

          <div className="feature-item">
            <FiCreditCard className="feature-icon" />
            <div className="feature-content">
              <h4>{t("subscription.flexible_billing", lang)}</h4>
              <p>{t("subscription.flexible_billing_desc", lang)}</p>
            </div>
          </div>
        </div>

        <div className="pricing-section">
          <div className="price-display">
            <span className="currency">$</span>
            <span className="amount">24.99</span>
            <span className="period">/month</span>
          </div>
          <p className="price-description">
            {t("subscription.price_description", lang)}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="paywall-actions">
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleSubscribe}
            className="subscribe-button"
            block
          >
            {t("subscription.subscribe_now", lang)} - $24.99/month
          </Button>

          {user?.subscriptionStatus && user.subscriptionStatus !== "none" && (
            <Button
              size="large"
              onClick={handleManageSubscription}
              className="manage-button"
              block
            >
              {t("subscription.manage_subscription", lang)}
            </Button>
          )}

          <Button size="large" onClick={onClose} className="close-button" block>
            {t("subscription.maybe_later", lang)}
          </Button>
        </div>

        <div className="paywall-footer">
          <p className="security-note">
            {t("subscription.secure_payment", lang)}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionPaywallModal;
